import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import flowRight from 'lodash-es/flowRight';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import { useDescope } from '@descope/react-sdk';

import styles from './account-menu.styles';
import account from './account.svg?url';
import useSession from 'core/hooks/use-session';
import { signOut as cognitoLogout } from 'core/components/auth';
import { isSweepAccountsEnabled } from 'core/helpers/sweep-account';
import { getReportsUrl, isReportsEnabled } from 'core/helpers/reports';
import { isAgencyReportsVisible } from 'core/helpers/agency-reports';
import { getThemeVariant } from 'core/components/theme/theme-variant';
import PermissionsModal from './permissions-modal';
import ThemeSwitchDialog, { themeSwitchLabels } from './theme-switch-dialog';

const AccountMenu = ({ classes }) => {
  const { logout: descopeLogout } = useDescope();
  const session = useSession();
  const { canViewTools, canManageSweepAccount, canViewReports, isInternalAgent, canUseNewLook } = session;
  const [userMenuEl, setUserMenuEl] = useState(null);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [themeSwitchDialogOpen, setThemeSwitchDialogOpen] = useState(false);
  const history = useHistory();

  const handleClick = (event) => {
    setUserMenuEl(event.currentTarget);
  };

  const handleClose = () => {
    setUserMenuEl(null);
  };

  const handleToolsReroute = () => {
    history.push('/staff/tools');
  };

  const handleLogout = async () => {
    await cognitoLogout();
    await descopeLogout();
  };

  const goToProfile = () => {
    history.push('/profile');
  };

  const goToSweepAccount = () => {
    history.push('/sweep-account');
  };

  const goToAgencyReports = () => {
    history.push('/agency-reports');
  };

  // Executive management report opens in a new tab (external Vantage dashboard, not an
  // in-app route). noopener,noreferrer per the usual external-link hardening. (#35090)
  const reportsUrl = getReportsUrl();
  const goToReports = () => {
    window.open(reportsUrl, '_blank', 'noopener,noreferrer');
  };

  // Reports is internal-only: `canViewReports` is already gated to non-agency users in
  // getPermissions (auth.jsx), and the brand must have a configured reports URL. Agents
  // never see it. (#35090)
  const showReports = canViewReports && isReportsEnabled();

  // Sweep account management is gated per-brand/env and to the CanManageSweepAccount group
  // (agency principals). Producers use the account at checkout / manual charge but never
  // manage it. (#33155, #32606)
  const showSweepAccount = canManageSweepAccount && isSweepAccountsEnabled();

  // Agency Reports (#36219): brand `agencyReports.enabled` for internal users; agency users need the
  // brand's `visibleToAgencies` toggle plus CanViewAgencyReports (#35833) — see core/helpers/agency-reports.
  const showAgencyReports = isAgencyReportsVisible(session);

  return (
    <>
      <IconButton
        color="primary"
        aria-label="Profile"
        className={classes.buttonUser}
        onClick={handleClick}
        data-cy="menu"
      >
        <img src={account} alt="account" />
      </IconButton>
      <Menu anchorEl={userMenuEl} open={Boolean(userMenuEl)} onClose={handleClose} onClick={handleClose}>
        <MenuItem onClick={goToProfile}>Profile</MenuItem>
        {canViewTools && <MenuItem onClick={handleToolsReroute}>Tools</MenuItem>}
        {showReports && <MenuItem onClick={goToReports}>Reports</MenuItem>}
        {showAgencyReports && <MenuItem onClick={goToAgencyReports}>Agency Reports</MenuItem>}
        {showSweepAccount && <MenuItem onClick={goToSweepAccount}>Sweep Account</MenuItem>}
        {/* Internal users only — external agency users effectively just have ExternalSales (#31758) */}
        {isInternalAgent && <MenuItem onClick={() => setPermissionsModalOpen(true)}>Permissions</MenuItem>}
        {/* Internal staff try the new look before it rolls out to agency users and the mutuals */}
        {canUseNewLook && (
          <MenuItem
            onClick={() => {
              setThemeSwitchDialogOpen(true);
            }}
          >
            {themeSwitchLabels[getThemeVariant()]}
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
      {/* Rendered outside the Menu — the Menu closes itself on any item click */}
      {isInternalAgent && (
        <PermissionsModal open={permissionsModalOpen} onClose={() => setPermissionsModalOpen(false)} />
      )}
      {themeSwitchDialogOpen && <ThemeSwitchDialog open onClose={() => setThemeSwitchDialogOpen(false)} />}
    </>
  );
};

AccountMenu.propTypes = {
  classes: PropTypes.object.isRequired
};

export default flowRight(withStyles(styles))(AccountMenu);
